import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeSlash, CheckCircle } from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import { LuminexLockup } from '../components/LuminexLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PW_RULE = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error('Missing invite token'); return; }
    if (!PW_RULE.test(password)) {
      toast.error('Password must be 8+ chars with upper, lower, digit, and special.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/auth/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, confirmPassword: confirm }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed to set password');
      setDone(true);
      toast.success('Password set — you can sign in now');
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="accept-invite-page" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg-0)', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 460, background: 'var(--bg-1)', border: '1px solid var(--border-1)', borderRadius: 12, padding: '40px 36px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <LuminexLockup iconSize={32} showDevanagari={true} />
        </div>

        {done ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'inline-flex', width: 56, height: 56, borderRadius: '50%', background: 'rgba(14,124,107,0.12)', color: '#0E7C6B', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <CheckCircle size={32} weight="duotone" />
            </div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 600, color: 'var(--text-1)', marginBottom: 8, letterSpacing: '-0.01em' }}>You&apos;re all set</h2>
            <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 24 }}>Your password is saved and your email is verified.</p>
            <button className="btn btn-primary btn-block" onClick={() => navigate('/login')} data-testid="invite-goto-login-btn">Sign in</button>
          </div>
        ) : (
          <>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 600, color: 'var(--text-1)', marginBottom: 6, letterSpacing: '-0.01em', textAlign: 'center' }}>Set your password</h2>
            <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 24, textAlign: 'center' }}>Finish joining your workspace by choosing a strong password.</p>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                  <input type={showPw ? 'text' : 'password'} className="input" style={{ paddingLeft: 32, paddingRight: 36, fontSize: 13 }}
                    placeholder="Min 8 chars · upper · lower · digit · special"
                    value={password} onChange={e => setPassword(e.target.value)} required minLength={8} data-testid="invite-password-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)' }} data-testid="invite-toggle-pw-btn">
                    {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {password && (
                  <div data-testid="invite-password-strength" style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    {[
                      { k: '8+ chars', pass: password.length >= 8 },
                      { k: 'A-Z', pass: /[A-Z]/.test(password) },
                      { k: 'a-z', pass: /[a-z]/.test(password) },
                      { k: '0-9', pass: /\d/.test(password) },
                      { k: 'special', pass: /[^A-Za-z0-9]/.test(password) },
                    ].map(c => (
                      <span key={c.k} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 100, background: c.pass ? 'rgba(14,124,107,0.12)' : 'var(--bg-3)', color: c.pass ? '#0E7C6B' : 'var(--text-3)', fontWeight: 600 }}>
                        {c.pass ? '✓' : '·'} {c.k}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                  <input type={showPw ? 'text' : 'password'} className="input" style={{ paddingLeft: 32, fontSize: 13 }}
                    placeholder="Re-enter your password" value={confirm} onChange={e => setConfirm(e.target.value)} required data-testid="invite-confirm-password-input" />
                </div>
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={loading || !token} data-testid="invite-submit-btn">
                {loading ? <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : 'Set password & continue'}
              </button>
              {!token && <p style={{ textAlign: 'center', fontSize: 11, color: '#7A1F1F', marginTop: 10 }} data-testid="invite-missing-token">This invitation link is incomplete. Please ask your admin to re-send it.</p>}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
